import './Timeline.scss';
import TimelinePlayhead from '../../WorkspaceAssets/timeline-cursor.png';
import { getCustomisationValue, getGroupData } from '../EditablePreview/EditablePreview';

import WaveSurfer from 'wavesurfer.js';
import { useEffect, useRef, useState } from 'react';
import { WORKSPACE_LEFT_LIST_ICONS } from '../../WorkspaceAssets/Assets';

const waveImagesCache = {};
const clearOutOldWaveImagesFromCache = (featureId) => {
    // console.log("[timeline] Clearing out old wave images from cache for feature ID:", featureId);
    Object.keys(waveImagesCache).forEach(key => {
        if (!key.startsWith(`wf_${featureId}-`)) {
            delete waveImagesCache[key];
        }
    });
    // console.log("[timeline] Wave images cache after cleanup:", waveImagesCache);
};

const Timeline = (props) => {
    // console.log("Timeline component props:", props);
    useEffect(() => {
        clearOutOldWaveImagesFromCache(props.selectedFeatureId);
    }, [props.selectedFeatureId]);

    const TimelineItem = (props) => {
        const node = props.node;
        const steps = (1 + (node.config?.visualisationConfig?.endStep ?? node['end-step'] ?? 0) - (node.config?.visualisationConfig?.startStep ?? node['start-step'] ?? 0));
        const stepWidth = steps * 30 + (steps - 1) * 2 * 0.2; // Calculate the width based on the step range
        const positionRelativeToStep = props.positionRelativeToStep || 0;
        return (
            <div className={`v2-timeline-step ${(props.visualisedNode === node) ? 'v2-timeline-step-active' : ''}`} style={{ width: `${stepWidth}rem`, marginLeft: `${0.2 + ((node.config?.visualisationConfig?.startStep - positionRelativeToStep) * 30.4)}rem` }} onClick={() => props.activateNodeFunction(node)}>
                {node ? node.name : ''}
            </div>
        )
    }

    const TimelineAudioItem = (props) => {
        const audioData = props.audioData;
        const steps = (1 + (audioData['end-step'] ?? 0) - (audioData['trigger-step'] ?? 0));
        const stepWidth = steps * 30 + (steps - 1) * 2 * 0.2; // Calculate the width based on the step range
        const positionRelativeToStep = props.positionRelativeToStep || 0;

        const featureCustomisationVars = props.selectedFeatureNode?.customisationVars || {};
        console.log("TimelineAudioItem featureCustomisationVars:", featureCustomisationVars, audioData);
        const audioFileDataArray = getGroupData(featureCustomisationVars, audioData?.['audio-file'], -1);

        const getFirstEnabledAudioFileIndex = () => {
            let firstEnabledAudioFileIndex = audioData['audio-enabled'] ? -1 : 0;
            if (firstEnabledAudioFileIndex === -1 && audioFileDataArray?.length) {
                for (let i = 0; i < audioFileDataArray.length; i++) {
                    const isEnabled = getCustomisationValue(featureCustomisationVars, audioData?.['audio-enabled'], i, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
                    if (isEnabled) {
                        firstEnabledAudioFileIndex = i;
                        break;
                    }
                }
            }
            return firstEnabledAudioFileIndex;
        }

        const firstEnabledAudioFileIndex = getFirstEnabledAudioFileIndex();
        const audioFileData = getCustomisationValue(featureCustomisationVars, audioData?.['audio-file'], firstEnabledAudioFileIndex, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? null;

        // Generate a unique container ID for the waveform, making sure to sanitize the audio name
        const generateWaveformContainerId = () => {
            let id = `wf_${props.selectedFeatureId}-${firstEnabledAudioFileIndex}-audio-waveform-${audioData.name.replace(/\s+/g, '-')}`.replace(/[^a-zA-Z0-9_-]/g, '');
            id += '_' + (typeof audioFileData === 'string' ? audioFileData : audioFileData?.originalFile ?? 'no-file').replace(/\s+/g, '-').replace(/[^a-zA-Z0-9_-]/g, '');
            return id;
        }
        const [generatedWaveformContainerId, setGeneratedWaveformContainerId] = useState(generateWaveformContainerId());
        useEffect(() => {
            setGeneratedWaveformContainerId(generateWaveformContainerId());
            // console.log('[timeline, useEffect] Generated waveform container ID:', generatedWaveformContainerId);
        }, [audioData.name, props.selectedFeatureId, firstEnabledAudioFileIndex]);

        // console.log('Timeline Audio Item', audioFileData, generatedWaveformContainerId);

        const selectAudioGroup = () => {
            const audioGroupDataToActivate = {
                groupData: props.audioGroup,
                audioData: audioData,
            };
            props.activateTimelineAudioGroup(audioGroupDataToActivate);
        }

        const [wavesurferInstance, setWavesurferInstance] = useState(null);
        useEffect(() => {
            if (getFirstEnabledAudioFileIndex() !== -1 && audioFileData && audioFileData.srcUrl) {
                if (!waveImagesCache[generatedWaveformContainerId]) {
                    // console.log('[timeline, useEffect] Creating waveform for', generatedWaveformContainerId, 'with URL', audioFileData.srcUrl);
                    const waveSurfer = WaveSurfer.create({
                        container: `#${generatedWaveformContainerId}`,
                        waveColor: '#BCA4FF',
                        progressColor: '#BCA4FF',
                        url: audioFileData.srcUrl,
                        height: 20,
                        // responsive: true,
                        normalize: true,
                        hideScrollbar: true,
                        barWidth: 2,
                    });
                    waveSurfer.on('ready', () => {
                        waveSurfer.exportImage().then((dataUrl) => {
                            // console.log('[timeline, useEffect] Waveform image data URL:', dataUrl);
                            waveImagesCache[generatedWaveformContainerId] = dataUrl[0];
                            // console.log('[timeline, useEffect] Cached waveform image for', generatedWaveformContainerId, waveImagesCache);
                        });
                    });
                    setWavesurferInstance(waveSurfer);
                    return () => {
                        // console.log('[timeline, useEffect] Destroying waveform for', generatedWaveformContainerId);
                        waveSurfer.destroy();
                        setWavesurferInstance(null);
                    }
                }
            }
        }, [audioFileData, generatedWaveformContainerId, props.forceUpdate]);


        console.log("Rendering TimelineAudioItem for audio data:", audioData, "with audio file data:", audioFileData, props);

        return (
            <>
                {(firstEnabledAudioFileIndex !== -1 || props.activeTimelineAudioGroup?.audioData === audioData) && 
                    <div className={`v2-timeline-step audio-step ${(props.activeTimelineAudioGroup?.audioData === audioData) ? 'v2-timeline-step-active' : ''}`} style={{ width: `${stepWidth}rem`, marginLeft: `${0.2 + ((audioData['trigger-step'] - positionRelativeToStep) * 30.4)}rem` }} onClick={selectAudioGroup}>
                        <div className='v2-timeline-audio-waveform' id={generatedWaveformContainerId}>
                            {waveImagesCache[generatedWaveformContainerId] &&
                                <img src={waveImagesCache[generatedWaveformContainerId]} alt="Audio Waveform" />
                            }
                        </div>
                        <div className='v2-timeline-audio-label'>{audioData ? audioData.name : ''}</div>
                        <div className='v2-timeline-audio-label bottom'>[x{audioFileDataArray?.length ?? 1}]</div>
                    </div>
                }
                {(firstEnabledAudioFileIndex === -1 && props.activeTimelineAudioGroup?.audioData !== audioData) && 
                    <div className={`v2-timeline-step audio-step empty`} style={{ width: `${stepWidth}rem`, marginLeft: `${0.2 + ((audioData['trigger-step'] - positionRelativeToStep) * 30.4)}rem` }}>
                        <img src={WORKSPACE_LEFT_LIST_ICONS.add} alt="Add Audio" className='plus-icon' onClick={selectAudioGroup} />
                    </div>
                }
            </>
        )
    }

    const organiseTimelineItemsIntoRows = (items) => {
        const rows = [[]];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const itemStart = item.config?.visualisationConfig?.startStep ?? item['start-step'] ?? item.config?.visualisationConfig?.triggerStep ?? item['trigger-step'] ?? 0;
            const itemEnd = item.config?.visualisationConfig?.endStep ?? item['end-step'] ?? 0;
            let placed = false;
            if (item?.config?.groupSettings?.visualise === false) {
                continue;
            }
            for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
                const row = rows[rowIndex];
                let overlap = false;
                for (let j = 0; j < row.length; j++) {
                    const rowItem = row[j];
                    const rowItemStart = rowItem.config?.visualisationConfig?.startStep ?? rowItem['start-step'] ?? rowItem.config?.visualisationConfig?.triggerStep ?? rowItem['trigger-step'] ?? 0;
                    const rowItemEnd = rowItem.config?.visualisationConfig?.endStep ?? rowItem['end-step'] ?? 0;
                    if (itemStart < rowItemEnd && itemEnd > rowItemStart) {
                        overlap = true;
                        break;
                    }
                }
                if (!overlap) {
                    row.push(item);
                    placed = true;
                    break;
                }
            }
            if (!placed) {
                rows.push([item]);
            }
        }

        // Sort the rows based on the start step of their items
        rows.forEach(row => {
            row.sort((a, b) => {
                const aStart = a.config?.visualisationConfig?.startStep ?? a['start-step'] ?? a.config?.visualisationConfig?.triggerStep ?? a['trigger-step'] ?? 0;
                const bStart = b.config?.visualisationConfig?.startStep ?? b['start-step'] ?? b.config?.visualisationConfig?.triggerStep ?? b['trigger-step'] ?? 0;
                return aStart - bStart;
            });
        });

        return rows;
    }

    const [nodeRows, setNodeRows] = useState(props.selectedFeatureNode ? organiseTimelineItemsIntoRows(props.selectedFeatureNode.subNodes || []) : []);
    useEffect(() => {
        if (props.selectedFeatureNode) {
            setNodeRows(organiseTimelineItemsIntoRows(props.selectedFeatureNode.subNodes || []));
        } else {
            setNodeRows([]);
        }
        // console.log("[timeline, useEffect] Organised timeline node rows:", nodeRows);
    }, [props.selectedFeatureId]);

    const [audioGroupRows, setAudioGroupRows] = useState([]);
    useEffect(() => {
        if (props.selectedFeatureNode?.config?.timeline?.audioGroups) {
            const newAudioGroupRows = [];
            props.selectedFeatureNode.config.timeline.audioGroups.forEach((audioGroup, groupIndex) => {
                const audioItems = audioGroup.audios || [];
                const organisedAudioRows = organiseTimelineItemsIntoRows(audioItems);
                newAudioGroupRows[groupIndex] = organisedAudioRows;
            });
            setAudioGroupRows(newAudioGroupRows);
        } else {
            setAudioGroupRows([]);
        }
        // console.log("[timeline, useEffect] Organised timeline audio group rows:", audioGroupRows);
    }, [props.selectedFeatureId]);

    // console.log("Organised timeline node rows:", nodeRows);
    // console.log("Organised timeline audio group rows:", audioGroupRows);

    return (
        <div className='v2-timeline'>
            <div className="v2-timeline-row">
                <h4 className="v2-timeline-title">Feature Timeline</h4>
            </div>

            {props.selectedFeatureNode && 
                <div className="v2-timeline-holder">
                    <div className='v2-timeline-playhead' style={{ left: `${10.25 + props.timelinePlayheadStep * 30.4}rem`, top: '1.9rem' }} />
                    <div className="v2-timeline-row v2-timeline-sticky-header">
                        <div className="v2-timeline-row-label">
                            Feature
                        </div>
                        <div className="v2-timeline-inner-row">
                            <div className="v2-feature-name-block">
                                {props.selectedFeatureNode.name}
                            </div>
                        </div>
                    </div>
                    <div className="v2-timeline-row">
                        <div className="v2-timeline-row-label">
                            Steps
                        </div>
                        <div className="v2-timeline-inner-row">
                            {props.selectedFeatureNode.config?.timeline?.steps && props.selectedFeatureNode.config.timeline.steps.length > 0 &&
                                props.selectedFeatureNode.config.timeline.steps.map((step, index) => (
                                    <div key={index} className="v2-timeline-step" onClick={() => props.setTimelinePlayheadStep(index)}>
                                        {step}
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    <div className="v2-timeline-row">
                        <div className="v2-timeline-row-label">
                            Views
                        </div>
                        <div className="v2-timeline-inner-column">
                            {nodeRows && nodeRows.length > 0 &&
                                nodeRows.map((row, rowIndex) => (
                                    <div className='v2-timeline-inner-row' key={rowIndex}>
                                        {row.map((node, index) => {
                                            let positionRelativeToStep = 0;
                                            if (index > 0) {
                                                const prevItem = row[index - 1];
                                                const prevItemEndStep = prevItem.config?.visualisationConfig?.endStep ?? prevItem['end-step'] ?? 0;
                                                // We will need to add one so we position this item after the last one
                                                positionRelativeToStep = prevItemEndStep + 1;
                                            }
                                            return (
                                                <TimelineItem key={index} node={node} visualisedNode={props.visualisedNode} activateNodeFunction={props.activateNodeFunction} positionRelativeToStep={positionRelativeToStep} />
                                            )
                                        })}
                                    </div>
                                ))
                            }
                            {/*props.selectedFeatureNode.subNodes && props.selectedFeatureNode.subNodes.length > 0 &&
                                props.selectedFeatureNode.subNodes.map((node, index) => {
                                    // console.log("Rendering timeline item for view node:", node);
                                    if (node?.config?.groupSettings?.visualise === false) {
                                        return null;
                                    }
                                    return (
                                        <div className='v2-timeline-inner-row' key={index}>
                                            <TimelineItem node={node} visualisedNode={props.visualisedNode} activateNodeFunction={props.activateNodeFunction} />
                                        </div>
                                    )
                                })
                            */}
                        </div>
                    </div>
                    {props.selectedFeatureNode.config?.timeline?.audioGroups?.length > 0 &&
                        props.selectedFeatureNode.config?.timeline?.audioGroups.map((audioGroup, groupIndex) => {
                            if (audioGroup.audios && audioGroup.audios.length > 0) {
                                return (
                                    <div className="v2-timeline-row" key={groupIndex}>
                                        <div className="v2-timeline-row-label">
                                            Audio<br />[{audioGroup.name}]
                                        </div>
                                        <div className="v2-timeline-inner-column">
                                            {audioGroupRows[groupIndex] && audioGroupRows[groupIndex].length > 0 && (
                                                audioGroupRows[groupIndex].map((row, rowIndex) => (
                                                    <div className='v2-timeline-inner-row' key={groupIndex + '_' + rowIndex}>
                                                        {row.map((audio, audioIndex) => {
                                                            let positionRelativeToStep = 0;
                                                            if (audioIndex > 0) {
                                                                const prevItem = row[audioIndex - 1];
                                                                const prevItemEndStep = prevItem.config?.visualisationConfig?.endStep ?? prevItem['end-step'] ?? 0;
                                                                // We will need to add one so we position this item after the last one
                                                                positionRelativeToStep = prevItemEndStep + 1;
                                                            }
                                                            return (
                                                                <TimelineAudioItem key={groupIndex + '_' + rowIndex + '_' + audioIndex} audioGroup={audioGroup} audioData={audio} audioIndex={audioIndex} activateTimelineAudioGroup={props.activateTimelineAudioGroup} activeTimelineAudioGroup={props.activeTimelineAudioGroup} selectedFeatureNode={props.selectedFeatureNode} selectedFeatureId={props.selectedFeatureId} positionRelativeToStep={positionRelativeToStep} forceUpdate={props.forceUpdate} />
                                                            );
                                                        })}
                                                    </div>
                                                ))
                                            )}
                                            {/*audioGroup.audios.map((audio, audioIndex) => (
                                                <div className='v2-timeline-inner-row' key={audioIndex}>
                                                    <TimelineAudioItem audioData={audio} audioIndex={audioIndex} selectedFeatureNode={props.selectedFeatureNode} selectedFeatureId={props.selectedFeatureId} visualisedNode={props.visualisedNode} />
                                                </div>
                                            ))*/}
                                        </div>
                                    </div>
                                )
                            }
                            return null;
                        })
                    }
                </div>
            }
        </div>
    );
}

export default Timeline;
